import React from 'react';

import { TabsItemType } from "./components/tabs";
import { QuestionnaireManagementProps } from './questionnaire-management.types';
import { QuestionnaireHome } from './components/questionnaire-home';
import { Configuration } from './components/configuration';
import { StatusActions } from './components/status-actions';
import { QuestionnaireDesigner } from './components/questionnaire-designer';
import { EmailAndReminders } from './components/email-and-reminders';
import { QuestionnaireUpdateLocalDto } from '@modules/fhq-design-tool/dto/questionnaire-update-local-dto';
import { generateId } from '@common/utils/common';
import { QuestionnaireCreateLocalDto } from '@modules/fhq-design-tool/dto/questionnaire-create-local-dto';
import { I18n } from 'react-redux-i18n';
import { QuestionnairePageListDto } from '@common/dto/questionnaire/page/list-dto';
import { QuestionnairePageType } from '@common/dto/questionnaire/page/type';
import { QuestionnairePageCreateLocalDto } from '@modules/fhq-design-tool/dto/page-create-local-dto';
import { QuestionnaireFinalNotesPageGetDto } from '@common/dto/questionnaire/page/final-notes/get-dto';
import { Consent } from './components/consent';


export const useQuestionnaireManagementController = (props: QuestionnaireManagementProps) => {
  const { 
    markPageLoaded,
    validateQuestionnaire,
    onChange, 
    questionnaire, 
    getFolders, 
    showErrorMessage,
    getDatasheetFolderFormats,
    getFoldersForDatasheetFormats,
    errors,
    getFolderFields,
    getEmailTemplates,
    showConfirmMessage,
    getBaseFolders,
    getPage,
    isPageLoaded,
    getFhq4QuestionnaireField,
    getFolderFormats,
    getFormatFolders,
    onPageCopy,
    copiedPageFromPages
  } = props;

  const containerRef = React.useRef<HTMLDivElement | undefined>();

  const [localQuestionnaire, setLocalQuestionnaire] = React.useState<QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto | undefined>();
  const [currentTab, setCurrentTab] = React.useState<TabsItemType | undefined>();
  const [lastSaveQuestionnaireIndicator, setLastSaveQuestionnaireIndicator] = React.useState<string | undefined>();

  const extendQuestionnaire = React.useCallback(async (
    questionnaireToExtend: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto,
  ) => {
    try {
      const finalNotesPage = questionnaireToExtend.designerTab.pages.find((page) => (page as QuestionnairePageListDto).pageType === QuestionnairePageType.finalNotes);

      const finalNotesPageShouldBeUpdated = (
        finalNotesPage &&
        getPage &&
        !isPageLoaded(finalNotesPage.id as QuestionnairePageListDto['id']) &&
        !(finalNotesPage as QuestionnairePageCreateLocalDto).isNotCreated
      );

      if (finalNotesPageShouldBeUpdated) {
        const fullFinalNotesPage = await getPage<QuestionnaireFinalNotesPageGetDto>(finalNotesPage.id as QuestionnairePageListDto ['id']);

        const newQuestionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto = {
          ...questionnaireToExtend,
          designerTab: {
            ...questionnaireToExtend.designerTab,
            pages: questionnaireToExtend.designerTab.pages.map((page) => {
              if ((page as QuestionnairePageListDto).pageType === QuestionnairePageType.finalNotes) {
                return {
                  ...page,
                  fields: fullFinalNotesPage.fields,
                } as QuestionnaireFinalNotesPageGetDto;
              }

              return page;
            }),
          },
        };

        return {
          extendedQuestionnaireIfNeeded: newQuestionnaire,
          modified: true,
        };
      }

      return {
        extendedQuestionnaireIfNeeded: questionnaireToExtend,
        modified: false,
      };
    } catch (err) {
      console.error(err);
      showErrorMessage(I18n.t('Failed to get Final Notes page data'));
      return {
        extendedQuestionnaireIfNeeded: questionnaireToExtend,
        modified: false,
      };
    }
  }, [questionnaire, getPage, isPageLoaded]);

  React.useEffect(() => {
    if (questionnaire && !currentTab) {
      setCurrentTab(TabsItemType.questionnaireHome);
    }

    if ((questionnaire as QuestionnaireCreateLocalDto | undefined)?.isNotCreated && currentTab !== TabsItemType.questionnaireHome) {
      setCurrentTab(TabsItemType.questionnaireHome);
    }

    if (!questionnaire) {
      setCurrentTab(undefined);
    }

    setLocalQuestionnaire(questionnaire);

    if (questionnaire) {
      extendQuestionnaire(questionnaire).then(({ extendedQuestionnaireIfNeeded, modified }) => {
        if (modified) {
          setLocalQuestionnaire(extendedQuestionnaireIfNeeded);
          onChange(extendedQuestionnaireIfNeeded);
        }
  
        setLastSaveQuestionnaireIndicator(generateId());
      });
    } else {
      setLastSaveQuestionnaireIndicator(generateId());
    }
  }, [questionnaire]);

  const handleQuestionnaireChangeBase = React.useCallback(async (
    updatedQuestionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto,
    params?: { operationType?: 'addExistingInfo' | 'change' }
  ) => {
    setLocalQuestionnaire(updatedQuestionnaire);
    onChange(updatedQuestionnaire, params);
    validateQuestionnaire(updatedQuestionnaire);
  }, [onChange, validateQuestionnaire]);

  const handleQuestionnaireChange = React.useCallback((updatedQuestionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto) => {
    handleQuestionnaireChangeBase(updatedQuestionnaire, { operationType: 'change' });
  }, [handleQuestionnaireChangeBase]);

  const handleExistingPageLoadAndAddToQuestionnaire = React.useCallback((updatedQuestionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto) => {
    handleQuestionnaireChangeBase(updatedQuestionnaire, { operationType: 'addExistingInfo' });
  }, [handleQuestionnaireChangeBase]);

  const scrollContainerToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  const handleMakeUncompletedInactiveConfirm = React.useCallback(() => {
    setCurrentTab(TabsItemType.statusActions);
  }, []);

  const handleUnintendedIncompleteInactive = React.useCallback(() => {
    setCurrentTab(TabsItemType.emailAndReminders);
  }, []);

  const tabsConfig: Record<TabsItemType, { component: React.ReactNode }> = {
    [TabsItemType.consent]: {
      component: localQuestionnaire && (
        <Consent
          onExistingPageLoadAndAddToQuestionnaire={handleExistingPageLoadAndAddToQuestionnaire}
          getFhq4QuestionnaireField={getFhq4QuestionnaireField}
          lastSaveQuestionnaireIndicator={lastSaveQuestionnaireIndicator}
          errors={errors}
          isPageLoaded={isPageLoaded}
          scrollParentToTop={scrollContainerToTop}
          getFolders={getFolders}
          getFields={getFolderFields}
          showConfirmMessage={showConfirmMessage}
          showErrorMessage={showErrorMessage}
          questionnaire={localQuestionnaire}
          onChange={handleQuestionnaireChange}
        />
      ),
    },
    [TabsItemType.configuration]: {
      component: localQuestionnaire && (
        <Configuration
          onChange={handleQuestionnaireChange}
          questionnaire={localQuestionnaire}
          showErrorMessage={showErrorMessage}
        />
      ),
    },
    [TabsItemType.emailAndReminders]: {
      component: localQuestionnaire && (
        <EmailAndReminders
          onChange={handleQuestionnaireChange}
          questionnaire={localQuestionnaire}
          showErrorMessage={showErrorMessage}
          getEmailTemplates={getEmailTemplates}
          getFolders={getFolders}
          getFolderFields={getFolderFields}
          showConfirmMessage={showConfirmMessage}
          onMakeUncompletedInactiveConfirm={handleMakeUncompletedInactiveConfirm}
          errors={errors}
        />
      ),
    },
    [TabsItemType.questionnaireDesigner]: {
      component: localQuestionnaire && (
        <QuestionnaireDesigner
          onExistingPageLoadAndAddToQuestionnaire={handleExistingPageLoadAndAddToQuestionnaire}
          getFhq4QuestionnaireField={getFhq4QuestionnaireField}
          lastSaveQuestionnaireIndicator={lastSaveQuestionnaireIndicator}
          errors={errors}
          getPage={getPage}
          onPageCopy={onPageCopy}
          markPageLoaded={markPageLoaded}
          isPageLoaded={isPageLoaded}
          scrollParentToTop={scrollContainerToTop}
          getFolders={getFolders}
          getFields={getFolderFields}
          showConfirmMessage={showConfirmMessage}
          showErrorMessage={showErrorMessage}
          questionnaire={localQuestionnaire}
          onChange={handleQuestionnaireChange}
          copiedPageFromPages={copiedPageFromPages}
        />
      ),
    },
    [TabsItemType.questionnaireHome]: {
      component: localQuestionnaire && questionnaire && (
        <QuestionnaireHome
          initialQuestionnaire={questionnaire}
          errors={errors}
          getFhq4QuestionnaireField={getFhq4QuestionnaireField}
          showErrorMessage={showErrorMessage}
          getFolders={getFolders}
          getBaseFolders={getBaseFolders}
          questionnaire={localQuestionnaire}
          getDatasheetFolderFormats={getDatasheetFolderFormats}
          getFoldersForDatasheetFormats={getFoldersForDatasheetFormats}
          getFolderFields={getFolderFields}
          onChange={handleQuestionnaireChange}
          getFolderFormats={getFolderFormats}
          getFormatFolders={getFormatFolders}
        />
      ),
    },
    [TabsItemType.statusActions]: {
      component: localQuestionnaire && (
        <StatusActions
          onUnintendedIncompleteInactive={handleUnintendedIncompleteInactive}
          showConfirmMessage={showConfirmMessage}
          getEmailTemplates={getEmailTemplates}
          errors={errors}
          getBaseFolders={getBaseFolders}
          getFolders={getFolders}
          getFields={getFolderFields}
          showErrorMessage={showErrorMessage}
          questionnaire={localQuestionnaire}
          onChange={handleQuestionnaireChange}
          getFhq4QuestionnaireField={getFhq4QuestionnaireField}
        />
      ),
    },
  };

  const tabsErrors = React.useMemo<Record<TabsItemType, boolean> | undefined>(() => {
    if (!questionnaire) {
      return undefined;
    }

    const tabHasErrorsFromFields = (tabsErrors: Array<undefined | { text: string }[]>) => {
      return tabsErrors.some((x) => {
        if (x?.length) {
          return true;
        }
      });
    };

    const mapping: Record<TabsItemType, boolean> = {
      [TabsItemType.questionnaireHome]: tabHasErrorsFromFields([
        errors?.properties?.questionnaireName, 
        errors?.properties?.genericLinkDefaultFolder,
        errors?.properties?.patientEmail,
        errors?.properties?.patientFirstName,
        errors?.properties?.patientLastName,
        errors?.properties?.exitUrl,
        errors?.properties?.externalUrl,
        errors?.properties?.logoUrl,
      ]),
      [TabsItemType.consent]: tabHasErrorsFromFields([
        errors?.properties?.consentTitle,
      ]) || Object.keys(errors?.consentPage || {}).length > 0,
      [TabsItemType.configuration]: false,
      [TabsItemType.emailAndReminders]: tabHasErrorsFromFields([
        errors?.properties?.emailNotifyOnSubmit, 
        errors?.properties?.remindEveryValue, 
        errors?.properties?.remindEveryType, 
        errors?.properties?.remindEveryMax, 
        errors?.properties?.remindOnceValue,
        errors?.properties?.remindOnceType,
        errors?.properties?.remindOnceDateFieldName,
      ]),
      [TabsItemType.questionnaireDesigner]: Object.keys(errors?.pages || {}).length > 0,
      [TabsItemType.statusActions]: tabHasErrorsFromFields([
        errors?.properties?.sendEmailToStuffEmails, 
        errors?.properties?.makeInactiveAfterFrequency,
        errors?.properties?.makeInactiveAfterType,
        errors?.properties?.makeInactiveAfterDateFieldName,
        errors?.properties?.submitTargetFolder,
        errors?.properties?.reviewedTargetFolder,
        errors?.properties?.inviteTargetFolder,
        errors?.properties?.pednameFields,
      ]),
    };

    return mapping;
  }, [questionnaire, errors]);

  return {
    containerRef,
    tabsErrors,
    tabsConfig,
    currentTab,
    setCurrentTab,
  };
};